import React, { useEffect, useRef, useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import classes from "./PurchaseOptions.module.css";
import CommonButton from "../../ui/CommonButton";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import { IoChevronDownOutline, IoCloseOutline } from "react-icons/io5";
import { env } from "../../constants/env";
import { toast } from "react-toastify";
import ExpirationCountdown from "./ExpirationCountdown/ExpirationCountdown";
import { useUser } from "../../context/UserContext";

const PurchaseOptions = ({
  setShowPurchase,
  challengeTitle = "$50K",
  challengePrice = 350,
  accountSize,
  challengeType,
}) => {
  const token = localStorage.getItem("userToken");

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [propcover, setPropcover] = useState(false);
  const [martingale, setMartingale] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({ value: "card", label: "Credit/Debit card" });
  const [isLoading, setIsLoading] = useState(false);

  const [targetDate, setTargetDate] = useState(null);
  const [loadingTimer, setLoadingTimer] = useState(true); // ⬅️ new
  const [discountCode, setDiscountCode] = useState("");
  const [discountDetails, setDiscountDetails] = useState(null); // holds redeemed code data
  const [discountError, setDiscountError] = useState("");

  const { fetchUserData } = useUser();

  const stripe = useStripe();
  const elements = useElements();
  const dropdownRef = useRef();

  const pricingMap = {
    "1-Step": { "$10K": 125, "$25K": 250, "$50K": 350, "$100K": 500 },
    "2-Step": { "$10K": 75,  "$25K": 150, "$50K": 350, "$100K": 500 },
  };

  const pricing = pricingMap[challengeType] || pricingMap["1-Step"];

  const challenges = [
    { label: "$10K", price: pricing["$10K"] },
    { label: "$25K", price: pricing["$25K"] },
    { label: "$50K", price: pricing["$50K"] },
    { label: "$100K", price: pricing["$100K"] },
  ];

  const [selectedChallenge, setSelectedChallenge] = useState({
    label: "",
    price: 0,
  });

  const basePrice = selectedChallenge.price;
  const propcoverPrice = propcover ? basePrice * 0.2 : 0;
  const martingalePrice = martingale ? basePrice * 0.15 : 0;
  const discountPercent = discountDetails?.percentage || 0;
  const discountAmount = (basePrice * discountPercent) / 100;

  const total = basePrice - discountAmount + propcoverPrice + martingalePrice;

  // const targetDate = new Date("2026-08-14T21:00:00Z");

  const stripeInputOptions = {
    style: {
      base: {
        color: "#ffffff",
        fontSize: "14px",
        "::placeholder": {
          color: "#AFAFAF",
        },
      },
      invalid: {
        color: "#ff4d4f",
      },
    },
  };

  useEffect(() => {
    if (accountSize) {
      const challenge = challenges.find((c) => c.label === `$${accountSize}`);
      if (challenge) {
        setSelectedChallenge(challenge);
      } else {
        setSelectedChallenge({ label: `$${accountSize}`, price: 0 }); // fallback
      }
    }
  }, [accountSize]);

  useEffect(() => {
    const initTimer = async () => {
      try {
        const res = await fetch(`${env.apiUrl}/api/user/start-purchase-timer`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();

        if (data.expiration) {
          setTargetDate(new Date(data.expiration));
        }
      } catch (err) {
        console.error("Failed to init purchase timer", err);
      } finally {
        setLoadingTimer(false); // ✅ mark finished
      }
    };

    initTimer();
  }, [token]);

  const handleApplyDiscount = async () => {
    setDiscountError("");

    if (!discountCode.trim()) {
      setDiscountError("Enter a discount code");
      return;
    }

    try {
      const res = await fetch(`${env.apiUrl}/api/redeem/check-discount-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: discountCode }),
      });

      const data = await res.json();

      if (!data.valid) {
        setDiscountDetails(null);
        setDiscountError("Invalid or already used code.");
        return;
      }

      // SUCCESS
      setDiscountDetails(data.discount); // contains % + etc.
      toast.success("Discount applied!");
    } catch (err) {
      setDiscountError("Something went wrong");
      console.error(err);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (showComplete) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto"; // cleanup
    };
  }, [showComplete]);

  useEffect(() => {
    if (selectedPaymentMethod?.value === "paypal" && !window.paypal) {
      const script = document.createElement("script");
      script.src = `https://www.paypal.com/sdk/js?client-id=${env.paypalClientId}&currency=USD&intent=authorize`;
      script.async = true;
      script.onload = () => console.log("PayPal SDK loaded");
      document.body.appendChild(script);
    }
  }, [selectedPaymentMethod]);

  const checkAvailability = async () => {
    const res = await fetch(
      `${env.apiUrl}/api/purchase/check-account-availability`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountSize: selectedChallenge.label.replace("$", ""),
        }),
      }
    );

    const data = await res.json();

    if (!data.available) {
      toast.error(
        "No available trading accounts for the selected challenge at the moment."
      );
      return false;
    }

    return true;
  };

  const handleStripePayment = async () => {
    try {
      const res = await fetch(
        `${env.apiUrl}/api/purchase/create-payment-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: total * 100,
            metadata: {
              challenge: selectedChallenge.label.replace("$", ""),
              challengeType: challengeType?.toLowerCase(),
              propcover: propcoverPrice,
              martingale: martingalePrice,
              discountCode
            },
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || "Failed to create payment");
        return;
      }

      const { clientSecret } = data;

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        },
      });

      if (result.error) {
        toast.error(result.error.message);
      } else if (result.paymentIntent.status === "succeeded") {
        setShowComplete(true);
      }
    } catch (err) {
      console.error(err);
      alert("Payment failed");
    }
  };

  const handlePaypalPayment = async () => {
    try {
      // 1. Create order on backend, get orderID and approvalUrl
      const createRes = await fetch(
        `${env.apiUrl}/api/purchase/paypal/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: total,
            metadata: {
              challenge: selectedChallenge.label.replace("$", ""),
              propcover: propcoverPrice,
              martingale: martingalePrice,
              discountCode
            },
          }),
        }
      );

      const createData = await createRes.json();

      if (!createData.id || !createData.approvalUrl) {
        toast.error("Failed to create PayPal order.");
        return;
      }

      // 2. Redirect user to approval URL to approve payment
      window.location.href = createData.approvalUrl;

      // After approval, PayPal redirects user back to your app, where you capture the payment.
    } catch (err) {
      console.error(err);
      toast.error("PayPal payment creation failed");
    }
  };

  const handleCompletePayment = async () => {
    setIsLoading(true);
    // const isAvailable = await checkAvailability();
    // if (!isAvailable) return;

    if (selectedPaymentMethod?.value === "card") {
      await handleStripePayment();
    } else if (selectedPaymentMethod?.value === "paypal") {
      await handlePaypalPayment();
    }
    setIsLoading(false);
  };

  // --- RENDER ---
  if (loadingTimer) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <p className="text-white">Loading purchase options...</p>
      </div>
    );
  }

  if (!targetDate) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <p className="text-danger">Could not initialize purchase timer.</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {showComplete && (
        <div
          className={`d-flex justify-content-center align-items-center mt-5 ${classes.completeOverlay}`}
        >
          <div
            className={`d-flex flex-column align-items-center ${classes.boxedContainer}`}
          >
            <div className="d-flex justify-content-end w-100 pt-3 pe-3">
              <IoCloseOutline
                color="white"
                size={30}
                onClick={() => {fetchUserData();setShowComplete(false); setShowPurchase(false)}}
                role="button"
              />
            </div>

            <img src="/icons/check-icon.svg" alt="Success" />
            <p>You have successfully activated your funded account</p>
            {/* <span onClick={() => setShowPurchase(false)} role="button" tabIndex={0}>
          Back to home
        </span> */}
          </div>
        </div>
      )}
      <button
        className={`${classes.closeButton} d-flex justify-content-center align-items-center`}
        onClick={() => setShowPurchase(false)}
      >
        <IoCloseOutline color="white" />
      </button>
      <ExpirationCountdown targetDate={targetDate} amount={`$${basePrice}`} />
      <h2 className={`mb-3 ${classes.heading}`}>Claim funded Account</h2>
      <div className={classes.box}>
        <div className={classes.left}>
          <div className="d-flex align-items-start">
            <div className={classes.inputsWrapper}>
              <CustomDropdown
                label="Select payment method"
                value={selectedPaymentMethod}
                onChange={setSelectedPaymentMethod}
                options={[
                  { value: "card", label: "Credit/Debit card" },
                ]}
                placeholder="Select Payment"
                inputClass={classes.inputContainer}
              />

              {selectedPaymentMethod?.value === "card" && (
                <>
                  <div className="mt-2">
                    <label className={classes.label}>Card number</label>
                    <CardNumberElement
                      className={`text-white ${classes.stripeInputContainer}`}
                      options={stripeInputOptions}
                    />
                  </div>
                  <div className="row mt-2">
                    <div className="col-6">
                      <label className={classes.label}>Expiry</label>
                      <CardExpiryElement
                        className={classes.stripeInputContainer}
                        options={stripeInputOptions}
                      />
                    </div>
                    <div className="col-6">
                      <label className={classes.label}>CVV</label>
                      <CardCvcElement
                        className={classes.stripeInputContainer}
                        options={stripeInputOptions}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={classes.verticleDivider} />
          </div>
          {/* ) : (
            <>
              <h4 className={classes.sectionTitle}>Optional</h4>
              <div className={classes.optionWrapper}>
                <div className={classes.option}>
                  <label className={classes.optionLabel}>
                    <input
                      type="checkbox"
                      checked={propcover}
                      onChange={() => setPropcover(!propcover)}
                    />
                    <div className={classes.optionContent}>
                      <div className={`${classes.optionHeader} d-flex justify-content-between align-items-center mb-1`}>
                        <span className={classes.optionTitle}>Propcover</span>
                        <span className={classes.recommended}>Recommended</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <p className={classes.optionDescription}>
                          Trade risk free: get reimbursed when your funded
                          account hits drawdown. Terms Apply*
                        </p>
                        <span className={classes.percent}>+20%</span>
                      </div>
                    </div>
                  </label>
                </div>

                <div className={classes.option}>
                  <label className={classes.optionLabel}>
                    <input
                      type="checkbox"
                      checked={martingale}
                      onChange={() => setMartingale(!martingale)}
                    />
                    <div className={classes.optionContent}>
                      <div className={`${classes.optionHeader} d-flex justify-content-between align-items-center mb-1`}>
                        <span className={classes.optionTitle}>Martingale</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <p className={classes.optionDescription}>
                          Unlock martingale trading at a 1.5x multiplier cap;
                          for more aggressive strategies.
                        </p>
                        <span className={classes.percent}>+15%</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </>
          )} */}
        </div>

        <div className={classes.right}>
          <h4 className={classes.sectionTitle}>Summary</h4>
          <div
            className={classes.summaryRow}
            style={{ position: "relative" }}
            ref={dropdownRef}
          >
            <span
              className={`d-flex align-items-center gap-1 ${classes.challengeDropdownToggle}`}
              onClick={() =>
                !showPaymentOptions && setDropdownOpen(!dropdownOpen)
              }
            >
              {selectedChallenge.label} Challenge Account
              {/* {!showPaymentOptions && <IoChevronDownOutline color="#CECECE" />} */}
            </span>
            <span>${basePrice}</span>

            {/* {dropdownOpen && !showPaymentOptions && (
              <div className={classes.challengeDropdownMenu}>
                {challengeOptions
                  .filter((option) => option.label !== selectedChallenge.label)
                  .map((option) => (
                    <div
                      key={option.label}
                      onClick={() => {
                        setSelectedChallenge(option);
                        setDropdownOpen(false);
                      }}
                      className={classes.challengeDropdownItem}
                    >
                      {option.label} Challenge Account
                    </div>
                  ))}
              </div>
            )} */}
          </div>
          {discountDetails && (
            <div className={classes.summaryRow}>
              <span>Discount ({discountDetails.percentage}%)</span>
              <span>-${discountAmount.toFixed(2)}</span>
            </div>
          )}

          {propcover && (
            <div className={classes.summaryRow}>
              <span>Propcover</span>
              <span>${propcoverPrice}</span>
            </div>
          )}
          {martingale && (
            <div className={classes.summaryRow}>
              <span>Martingale</span>
              <span>${martingalePrice}</span>
            </div>
          )}

          <div className={classes.divider} />
          <div className={`${classes.summaryRow} ${classes.total}`}>
            <span>Total</span>
            <span>${total}</span>
          </div>

          {/* Discount Input Box */}
          <div className={classes.discountBox}>
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder="PF-XX-XXXX"
              className={classes.discountInput}
              disabled={!!discountDetails}
            />

            {discountDetails ? (
              <div className={classes.appliedTag}>Applied</div>
            ) : (
              <button
                className={classes.applyBtn}
                onClick={handleApplyDiscount}
              >
                Apply
              </button>
            )}
          </div>

          {discountError && (
            <p className={classes.errorText}>{discountError}</p>
          )}

          {/* {!showPaymentOptions ? (
            <CommonButton
              type="button"
              text={`Pay $${basePrice}`}
              className={classes.continueBtn}
              onClick={() => setShowPaymentOptions(true)}
              disabled={false}
            />
          ) : ( */}
          <CommonButton
            type="button"
            text={isLoading ? "Loading..." : `Pay $${basePrice}`}
            className={classes.continueBtn}
            onClick={handleCompletePayment}
            disabled={!selectedPaymentMethod || isLoading}
          />
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOptions;
